/**
 * ChatPanelShell — the ONE launcher + panel shell for the floating chat widget.
 *
 * WHY THIS EXISTS (#903, epic #907, direction doc §5). The widget used to render
 * its shell four times: Stevens desktop panel, Stevens mobile sheet, Williams
 * desktop panel, Williams mobile sheet. Four copies of the same header meant the
 * expand control, the new-conversation control and the focus contract had to be
 * written four times, so they were written zero times. There is one render path
 * here and the desktop/mobile difference is expressed in `md:` utilities on the
 * same element, not in a second copy of the markup.
 *
 * The header carries three controls in stable positions (direction doc §5):
 * expand/contract, new conversation (when the surface offers one), minimize.
 * Minimize is `ChevronDown` on the desktop panel and `X` on the mobile sheet —
 * one button, one handler, two icons, because collapse-to-launcher reads as
 * "minimize" against a docked panel and as "close" against a sheet.
 *
 * Accessibility contract (direction doc §9): Escape closes; focus moves into
 * the panel on open and returns to the launcher on close; the launcher carries
 * `aria-expanded`. Motion is `motion-safe:` so the global reduced-motion guard
 * in index.css applies.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, ChevronDown, X, Maximize2, Minimize2, SquarePen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { COARSE_HIT_28 } from '@/lib/touch-target';
import { LAUNCHER_TUCK_CLASS, useLauncherTuck } from './launcherTuck';

/** Sits beside `stevens_widget_open` — the panel remembers its size (§5). */
export const EXPANDED_STORAGE_KEY = 'stevens_widget_expanded';

/**
 * Remembered compact/expanded state. Lives here rather than in the widget so
 * both persona branches read and write the same key with the same guards:
 * Safari private mode throws on `localStorage`, and a chat panel must still
 * open when it does.
 */
export function useExpandedPanel(): { expanded: boolean; toggleExpanded: () => void } {
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(EXPANDED_STORAGE_KEY, String(next));
      } catch {
        /* storage unavailable — the size just doesn't persist */
      }
      return next;
    });
  }, []);

  return { expanded, toggleExpanded };
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ChatPanelShellProps {
  open: boolean;
  onOpen: () => void;
  /** Collapse to the launcher. Minimize and close are the same action (§5). */
  onClose: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Omitted ⇒ no new-conversation control is rendered at all. */
  onNewConversation?: () => void;
  title: string;
  /** Inline status beside the title — "Listening…", "Connecting…". */
  headerStatus?: ReactNode;
  /** Persona-specific header controls (e.g. the parent speaker toggle). */
  headerExtras?: ReactNode;
  /** Bottom offset for the launcher — parents must clear the BottomNav. */
  launcherOffsetClass: string;
  /**
   * Suppresses the launcher while it is closed. Used when the BottomNav's More
   * sheet is open (M2.11): a FAB floating on top of a modal sheet is noise.
   */
  launcherHidden?: boolean;
  /** Transcript area. Rendered in a `flex-1 min-h-0` column so a scrolling
   *  transcript scrolls inside the panel instead of growing it. */
  children: ReactNode;
  chips?: ReactNode;
  composer: ReactNode;
  launcherAriaLabel: string;
}

export function ChatPanelShell({
  open,
  onOpen,
  onClose,
  expanded,
  onToggleExpanded,
  onNewConversation,
  title,
  headerStatus,
  headerExtras,
  launcherOffsetClass,
  launcherHidden = false,
  children,
  chips,
  composer,
  launcherAriaLabel,
}: ChatPanelShellProps) {
  const { t } = useTranslation('chat');
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(open);
  const [hasEntered, setHasEntered] = useState(false);
  // #2500 / #2505 / #2495: slide to the screen edge while a page control is
  // under the launcher's home position (launcherTuck.ts).
  const tucked = useLauncherTuck(launcherRef, !open && !launcherHidden);

  // Entrance animation for the launcher — preserved from the widget it replaces.
  useEffect(() => {
    const timer = setTimeout(() => setHasEntered(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Focus in on open, back to the launcher on close. Only on the transition —
  // a panel restored open on page load must not steal focus from the page.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;

    if (open && !wasOpen) {
      const panel = panelRef.current;
      if (!panel) return;
      // A composer that auto-focuses itself has already put focus inside the
      // panel; moving it back up to the header would be a regression, so the
      // shell only places focus when nothing in the panel has claimed it.
      if (panel.contains(document.activeElement)) return;
      panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
      return;
    }

    if (!open && wasOpen) {
      launcherRef.current?.focus();
    }
  }, [open]);

  // The band is `bg-stevens-indigo-solid`, so its hover rung is the token
  // `-solid-2` — the same pairing the launcher FAB uses. The four `bg-white/10`
  // hovers this shell replaces were the old, un-themed spelling of it.
  //
  // #2644 (D-2420): each header button draws 28 px; on a coarse pointer an
  // invisible ::after reaches 8 px past every edge (44 px) at -z-10, under the
  // `isolate` header group below, which spaces the buttons 16 px apart (twice
  // the reach) so neighbouring hit areas meet edge to edge. `headerExtras`
  // (the conversation-history button) joins the same group with the same reach.
  const headerButtonClass = cn(
    'p-1.5 rounded hover:bg-stevens-indigo-solid-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current',
    COARSE_HIT_28,
  );

  return (
    <>
      {!open && !launcherHidden && (
        <button
          ref={launcherRef}
          type="button"
          onClick={onOpen}
          aria-expanded={open}
          aria-label={launcherAriaLabel}
          title={launcherAriaLabel}
          data-tucked={tucked ? 'true' : 'false'}
          className={cn(
            // z-40, not the open panel's z-[9990] (#2438, the shell stacking
            // contract of D-2398): above page content, below the z-50 scrim
            // and content of every Radix dialog, so an open dialog dims the
            // launcher instead of the launcher covering the dialog's buttons.
            // The More drawer (z-[60]) and toasts (z-[100]) also sit above it.
            'fixed z-40 flex items-center justify-center rounded-full shadow-lg transition-all duration-300',
            'bg-stevens-indigo-solid text-stevens-indigo-solid-fg hover:bg-stevens-indigo-solid-2 hover:shadow-xl',
            'w-[52px] h-[52px]',
            // bottom stacks above env(safe-area-inset-bottom) so the FAB clears
            // the Android gesture bar / iOS Home indicator on native and the
            // page-bottom edge on web. Parents get an extra lift for the
            // BottomNav — the caller precomputes which.
            'right-5',
            launcherOffsetClass,
            hasEntered ? 'opacity-100 scale-100' : 'opacity-0 scale-90',
            tucked && LAUNCHER_TUCK_CLASS,
          )}
        >
          <Shield className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('widget.panelLabel', { name: title })}
          className={cn(
            'fixed z-[9990] flex flex-col bg-background shadow-2xl',
            // Below md: a bottom sheet, full width, docked to the bottom edge.
            'left-0 right-0 bottom-0 border-t rounded-t-xl',
            expanded ? 'h-[calc(100dvh-3rem)]' : 'h-[70vh]',
            // md and up: a docked panel in the bottom-right corner.
            'md:left-auto md:right-5 md:bottom-5 md:border md:rounded-xl',
            expanded
              ? 'md:w-[min(720px,calc(100vw-2.5rem))] md:h-[min(85vh,900px)]'
              : 'md:w-[380px] md:h-[520px]',
            'motion-safe:animate-in motion-safe:slide-in-from-bottom motion-safe:duration-300',
          )}
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-stevens-indigo-solid text-stevens-indigo-solid-fg rounded-t-xl">
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="h-4 w-4 flex-shrink-0 opacity-90" />
              <span className="font-medium text-body truncate">{title}</span>
              {headerStatus}
            </div>

            <div
              data-testid="widget-header-actions"
              className="isolate flex items-center gap-1 [@media(pointer:coarse)]:gap-4 flex-shrink-0"
            >
              {headerExtras}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onToggleExpanded}
                    aria-label={expanded ? t('widget.contract') : t('widget.expand')}
                    aria-pressed={expanded}
                    className={headerButtonClass}
                  >
                    {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{expanded ? t('widget.contract') : t('widget.expand')}</TooltipContent>
              </Tooltip>

              {onNewConversation && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={onNewConversation}
                      aria-label={t('widget.newConversation')}
                      className={headerButtonClass}
                    >
                      <SquarePen className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('widget.newConversation')}</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('widget.minimize')}
                    className={headerButtonClass}
                  >
                    <ChevronDown className="hidden md:block h-4 w-4" />
                    <X className="md:hidden h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('widget.minimize')}</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* The transcript owns its own scrolling; `min-h-0` is what stops a
              long thread from pushing the chips and composer off the panel. */}
          <div className="flex-1 min-h-0 flex flex-col">{children}</div>

          {chips}
          {composer}
        </div>
      )}
    </>
  );
}
